import { window } from "global";
import React, { useState, useEffect } from "react";
import classnames from "classnames";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { NewAppDownloadButton } from "components/Buttons/AppDownloadButton";
import AppFeature, {
  AppFeatureProps,
  deadlockFeatureCards,
  LolFeatureCards,
  valFeatureCards,
} from "@ugg/shared/components/common/AppFeature";
import { appFilename } from "lib/file-download";
import { SvgGameLol } from "@ugitgud/legos/ui/icon/SVG/GameLol";
import { SvgDeadlockLogo } from "@ugitgud/legos/ui/icon/SVG/DeadlockLogo";
import { SvgGameVal } from "@ugitgud/legos/ui/icon/SVG/GameVal";
import { Icon as SvgIcon } from "@ugitgud/legos/ui/icon/Icon";
import { Helmet } from "react-helmet-async";
import { SupportedGames } from "@ugitgud/legos/ui/nav/GameSelector";
import { ReactComponent as RiotIcon } from "@ugg/shared/assets/svg/riot-icon-uncolored.svg";
import { DeadlockFeatureSpotlight } from "components/DesktopAppSplashPage/DeadlockFeatureSpotlight";

function toHex(str: string) {
  let h = "";
  for (let i = 0; i < str.length; i++) {
    h += str.charCodeAt(i).toString(16);
  }
  return h;
}

type AppSupportedGames = Extract<SupportedGames, "lol" | "val" | "deadlock">;

type GameConfig = {
  game: AppSupportedGames;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  beta?: boolean;
  new?: boolean;
  featureCards: AppFeatureProps[];
  SpotlightComponent?: React.ReactElement;
};

const gameConfigs: Record<AppSupportedGames, GameConfig> = {
  lol: {
    game: "lol",
    label: "League of Legends",
    Icon: SvgGameLol,
    featureCards: LolFeatureCards,
  },
  val: {
    game: "val",
    label: "Valorant",
    Icon: SvgGameVal,
    featureCards: valFeatureCards,
  },
  deadlock: {
    game: "deadlock",
    label: "Deadlock",
    Icon: SvgDeadlockLogo,
    new: true,
    featureCards: deadlockFeatureCards,
    SpotlightComponent: <DeadlockFeatureSpotlight />,
  },
};

type DesktopSplashPageProps = {
  game?: AppSupportedGames;
};

export const DesktopAppSplashPage: React.FC<DesktopSplashPageProps> = () => {
  const queryString = useQueryString();
  const gameFromQuery = (queryString.game as AppSupportedGames) || "lol";
  const [tab, setTab] = useState<AppSupportedGames>(gameFromQuery);

  useEffect(() => {
    window?.scrollTo(0, 0);
    // Remove skin ads
    try {
      window.top.document.getElementById("siteSkinContainer").remove();
    } catch (e) {}
  }, []);

  return (
    <div className="desktop-app-splash-page">
      <Helmet>
        <title>U.GG App</title>
      </Helmet>
      <div className="desktop-app-splash-page_width-container">
        <div className="video-bg">
          <div className="overlay" />
          <video key={tab} playsInline autoPlay loop muted>
            <source
              type="video/webm"
              src={
                tab === "deadlock"
                  ? "https://static.bigbrain.gg/assets/ugg/pages/desktop-app/deadlock_promo.webm"
                  : "https://static.bigbrain.gg/assets/ugg/pages/desktop-app/ugg_lol_val_promo.webm"
              }
            />
          </video>
        </div>
        <div className="desktop-app-splash-page_header">
          <h1>
            <span className="text-[30px]">One app, Three Games</span>
            <br />
            <strong className="text-[36px]">Dozens of Features.</strong>
          </h1>
          <h2>
            The <strong className="text-white">new U.GG app</strong> is available, combining both our LoL, Deadlock, <br />&
            Valorant products into one free, seamless experience.
          </h2>
          <div className="download-row">
            {window && (
              <NewAppDownloadButton
                url="https://u.gg/get-windows-app"
                filename={appFilename(toHex((queryString.utm_campaign || "").toLowerCase()))}
                label="Download App"
                icon="cloud"
              />
            )}
          </div>
          <a className="block mt-[24px] text-[14px]" href="https://u.gg/get-windows-app" download>
            Download button not working? <span className="text-accent-blue-400 underline">Click here.</span>
          </a>
          <div className="flex gap-x-[8px] mt-[24px] items-center">
            <RiotIcon className="w-[20px] h-[20px] text-[#CDDCFE]" />
            <span style={{ fontSize: 14, color: "#CDDCFE" }}>Riot Games Compliant</span>
          </div>
        </div>
        <div className="app-tabs flex items-center gap-[12px] mt-[18px]">
          {Object.values(gameConfigs).map((appTab) => {
            return (
              <GameAppTab
                key={appTab.game}
                {...appTab}
                isActive={tab === appTab.game}
                onClick={() => setTab(appTab.game)}
                isNew={appTab.new}
              />
            );
          })}
        </div>
        {gameConfigs[tab].SpotlightComponent}
        <div className="desktop-app-splash-page_features-container">
          <div className="features-container_header">
            <h1>{gameConfigs[tab].label} Features</h1>
          </div>
          <div className="features-grid">
            {gameConfigs[tab].featureCards.map((feature, index) => {
              return <AppFeature key={index} {...feature} />;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

type GameAppTabProps = {
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isActive: boolean;
  beta?: boolean;
  isNew?: boolean;
  onClick: () => void;
};

const GameAppTab: React.FC<GameAppTabProps> = ({ label, Icon, beta, isNew, isActive, onClick }) => {
  const [hover, setHover] = useState(false);

  return (
    <button
      className={classnames("flex items-center justify-center px-[12px] py-[10px] rounded-[3px] bg-purple-400 font-['Inter']", {
        "!bg-purple-100": hover,
        "!bg-purple-100 border-t-[1px] border-[#575792]": isActive,
      })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {isActive || hover ? (
        <SvgIcon className="w-[18px]" icon={<Icon />} />
      ) : (
        <SvgIcon className="w-[18px] text-accent-gray-100" icon={<Icon />} />
      )}
      <div
        className={classnames("ml-[8px] text-[13px] text-lavender-50", { "text-white font-bold": isActive, "text-white": hover })}
      >
        {label}
      </div>
      {beta && (
        <div className="flex items-center ml-[8px] px-[4px] h-[14px] rounded-[1px] bg-accent-forest-green-500 font-bold text-[10px]">
          BETA
        </div>
      )}
      {isNew && (
        <div className="flex items-center ml-[8px] px-[4px] h-[14px] rounded-[1px] bg-accent-blue-400 font-bold text-[10px]">
          NEW
        </div>
      )}
    </button>
  );
};

export default DesktopAppSplashPage;
